import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import { logger } from 'shared/lib'
import { RequestLoadData } from 'shared/api/lib'
import { AiCallingApi } from 'entities/AICalling/api/aiCallingApi'
import { type IParamsGetList, type IResponseGetList } from 'entities/AICalling/api/types'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { CallingListItem } from './CallingListItem'

export class CallingStore {
  readonly tableStore
  private _disposeParamsWithoutPage: IReactionDisposer | null = null
  private _disposeParamsGetItems: IReactionDisposer | null = null
  private _initialLoading = false
  private _search = ''
  private _itemsMap: Map<number, CallingListItem> = new Map()

  constructor(readonly entities: IEntitiesStore) {
    this.tableStore = new TableStore<CallingListItem>({
      key: 'callingAiAgentTable',
      limit: 10,
      page: 1,
    })
    makeAutoObservable(this)
  }

  init = () => {
    this.reactionParamsWithoutPage()
    this.reactionParamsGetItems()
    return this.dispose
  }

  dispose = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsGetItems?.()
    this._requestLoadData.dispose()
  }

  reactionParamsWithoutPage = () => {
    this._disposeParamsWithoutPage?.()
    this._disposeParamsWithoutPage = reaction(
      () => this.paramsWithoutPage,
      () => {
        this.tableStore.setLoading(true)
        this.tableStore.setPage(1)
      }
    )
  }

  reactionParamsGetItems = () => {
    this._disposeParamsGetItems?.()
    this._disposeParamsGetItems = reaction(
      () => this.paramsGetItems,
      () => this._requestLoadData.debounceLoadData()
    )
  }

  get paramsWithoutPage(): Omit<IParamsGetList, 'page'> {
    return {
      limit: this.tableStore.limit || undefined,
      search: this.search || undefined,
    }
  }

  get paramsGetItems(): IParamsGetList {
    return {
      offset: (this.tableStore.page - 1) * (this.paramsWithoutPage.limit || 0),
      ...this.paramsWithoutPage,
    }
  }

  get initialLoading() {
    return this._initialLoading
  }

  private _requestLoadData = new RequestLoadData({
    requestCallback: async ({ cancelRequest }) => {
      this.tableStore.setLoading(true)
      const { data } = await AiCallingApi.getList(this.paramsGetItems, {
        cancelToken: cancelRequest.token,
      })
      this.setData(data)
    },
    finallyCallback: () => {
      runInAction(() => {
        this._initialLoading = false
        this.tableStore.setLoading(false)
      })
    },
  })

  loadData = async () => {
    await this._requestLoadData.loadData()
  }

  setData = ({ items, total }: IResponseGetList) => {
    this._itemsMap.clear()
    items.forEach((item) => {
      const callingListItem = new CallingListItem(item, this.entities)
      this._itemsMap.set(callingListItem.id, callingListItem)
    })
    this.tableStore.setTotal(total)
    const ids = items.map((item) => item.id)
    if (ids.length) {
      this._loadAgentsCallsCount(ids)
    }
  }

  private _loadAgentsCallsCount = async (ids: number[]) => {
    try {
      const { data } = await AiCallingApi.getAgentsCallsCount(ids)
      this.items.forEach((item) => {
        const count = data[item.id]
        if (typeof count === 'number') {
          item.setTotalCals(count)
        }
      })
    } catch (e) {
      logger.error(e)
    }
  }

  get search() {
    return this._search
  }

  setSearch = (value: string) => {
    this._search = value
  }

  get items() {
    return Array.from(this._itemsMap.values())
  }

  get isEmpty() {
    if (this.tableStore.loading) return false
    if (this.search) {
      return false
    }
    return !this._itemsMap.size
  }

  onSuccessDelete = (id: number) => {
    this._itemsMap.delete(id)
  }
}
